import { ref, computed, type Ref } from 'vue';
import Http from '@/services/Http';
import type { StatusDefinition, SaveStatusRequest } from '@/models/StatusDefinitionModel';

const cache: Record<string, Ref<StatusDefinition[]>> = {};

/**
 * Composable for managing status definitions from the unified Options-based system.
 * 
 * Usage:
 * const { 
 *   statuses,           // All statuses for the category
 *   terminalStatuses,   // IsRefActive = false (Completed, Cancelled, etc.)
 *   activeStatuses,     // IsActive = true (available for use)
 *   isRefActive,        // (statusId) => boolean
 *   isStatusActive,     // (statusId) => boolean
 *   getStatus,          // (statusId) => StatusDefinition | undefined
 *   saveStatus,         // (status) => Promise<void>
 *   deleteStatus,       // (statusId) => Promise<void>
 *   reloadStatuses      // () => Promise<void>
 * } = useStatusManager('WorkOrder');
 */
export function useStatusManager(category: string) {
  // Initialize cache for this category if not exists
  if (!cache[category]) {
    cache[category] = ref<StatusDefinition[]>([]);
  }
  
  const statuses = cache[category];
  const loading = ref(false);
  const error = ref<string | null>(null);

  // Filtered computed properties
  const terminalStatuses = computed(() => 
    statuses.value.filter(s => s.IsRefActive === false && s.IsActive)
  );

  const activeStatuses = computed(() => 
    statuses.value.filter(s => s.IsActive).sort((a, b) => a.SortOrder - b.SortOrder)
  );

  const refActiveStatuses = computed(() => 
    statuses.value.filter(s => s.IsRefActive === true && s.IsActive)
  );

  /**
   * Load statuses from API
   */
  const loadStatuses = async () => {
    loading.value = true;
    error.value = null;
    
    try {
      // const response = await Http.get(`/Status/List/${category}`);
      
      const response: any[] = [];
      if (response && Array.isArray(response)) {
        statuses.value = response;
      }
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to load statuses';
      console.error(`Error loading statuses for ${category}:`, err);
    } finally {
      loading.value = false;
    }
  };

  /**
   * Reload statuses (force refresh)
   */
  const reloadStatuses = async () => {
    await loadStatuses();
  };

  /**
   * Get a single status by ID
   */
  const getStatus = (statusId: number): StatusDefinition | undefined => {
    return statuses.value.find(s => s.Id === statusId);
  };

  /**
   * Check if a status means the referenced entity is "active"
   * Returns true if entity is active, false if terminal (completed/cancelled)
   */
  const isRefActive = (statusId: number): boolean => {
    const status = getStatus(statusId);
    return status?.IsRefActive ?? true; // Default to true if not found
  };

  /**
   * Check if a status definition is enabled/available
   */
  const isStatusActive = (statusId: number): boolean => {
    const status = getStatus(statusId);
    return status?.IsActive ?? false;
  };

  /**
   * Check if a status is terminal (IsRefActive = false)
   */
  const isTerminal = (statusId: number): boolean => {
    return !isRefActive(statusId);
  };

  /**
   * Get terminal status IDs (for queries like "find completed jobs")
   */
  const getTerminalStatusIds = (): number[] => {
    return terminalStatuses.value.map(s => s.Id);
  };

  /**
   * Get active (non-terminal) status IDs (for queries like "find open jobs")
   */
  const getActiveStatusIds = (): number[] => {
    return refActiveStatuses.value.map(s => s.Id);
  };

  /**
   * Save a status (create or update)
   */
  const saveStatus = async (status: SaveStatusRequest): Promise<void> => {
    loading.value = true;
    error.value = null;
    
    try {
      await Http.post('/Status/Save', { ...status, Category: category });
      await reloadStatuses(); // Refresh cache
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to save status';
      console.error(`Error saving status for ${category}:`, err);
      throw err;
    } finally {
      loading.value = false;
    }
  };

  /**
   * Delete (soft delete) a status
   */
  const deleteStatus = async (statusId: number): Promise<void> => {
    loading.value = true;
    error.value = null;
    
    try {
      await Http.post(`/Status/Delete/${category}/${statusId}`);
      await reloadStatuses(); // Refresh cache
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to delete status';
      console.error(`Error deleting status ${category}/${statusId}:`, err);
      throw err;
    } finally {
      loading.value = false;
    }
  };

  /**
   * Seed default statuses (admin only)
   */
  const seedDefaults = async (): Promise<void> => {
    loading.value = true;
    error.value = null;
    
    try {
      await Http.post('/Status/SeedDefaults');
      await reloadStatuses();
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to seed defaults';
      console.error(`Error seeding default statuses:`, err);
      throw err;
    } finally {
      loading.value = false;
    }
  };

  // Auto-load on first use if cache is empty
  if (statuses.value.length === 0) {
    loadStatuses();
  }

  return {
    // Data
    statuses,
    terminalStatuses,
    activeStatuses,
    refActiveStatuses,
    loading,
    error,
    
    // Getters
    getStatus,
    isRefActive,
    isStatusActive,
    isTerminal,
    getTerminalStatusIds,
    getActiveStatusIds,
    
    // Actions
    loadStatuses,
    reloadStatuses,
    saveStatus,
    deleteStatus,
    seedDefaults,
  };
}
